import { zFightingFixDistance } from "@dextall/panels-defaults";
import { BoundaryLoopsFactory } from "../boundaries/boundaryLoopsFactory";
import customPanelsColorFactory from "../colors/customComponentColorsFactory";
import { editorCladdingGeometryThickness } from "../defaults";
import {
    CladdingCellExtrusionGeometryFactory,
    type ExtrusionGeometry,
} from "../geometry/claddingCellExtrusionGeometryFactory";
import { FacadeElementType } from "./facadeElementType";
import type { UpdateCustomPanelOffsetCommand } from "../../../commands/updateCustomPanelOffsetCommand";
import type { ICustomPanelSource } from "../../../responses/customPanelSource";
import type { BoundaryLoop } from "../boundaries/boundaryLoop";
import type { CardinalDirection } from "../geometry/compass";
import type { Depth, InstallationSequence, PanelParameters } from "./panelParameters";

export class CustomPanel {
    private readonly thickness = editorCladdingGeometryThickness;
    private readonly material = customPanelsColorFactory.getDefaultMaterial();
    private readonly _depth: Depth;
    private _installationSequence: InstallationSequence;

    constructor(
        private readonly panelSource: ICustomPanelSource,
        private _panelBoundaryLoop: BoundaryLoop,
        public readonly panelDbId: number,
        private readonly claddingExtrusionFactory: CladdingCellExtrusionGeometryFactory,
        public readonly facadeOrientation: CardinalDirection,
        public readonly internalId?: string,
    ) {
        this._installationSequence = panelSource.installationSequence;
        this._depth = panelSource.depth;
        // this id is used to keep original panel id after undo or redo actions with panel
        this.internalId = internalId || panelSource.id;
    }

    get id(): string {
        return this.panelSource.id;
    }

    get elementType(): FacadeElementType.CustomPanel {
        return FacadeElementType.CustomPanel;
    }

    get installationSequence(): InstallationSequence {
        return this._installationSequence;
    }

    get depth(): Depth {
        return this._depth;
    }

    get customPanelTypeId(): string {
        return this.panelSource.customPanelTypeId;
    }

    get wallFaceId(): string {
        return this.panelSource.wallFaceId;
    }

    get length(): number {
        return this.panelSource.length;
    }

    get height(): number {
        return this.panelSource.height;
    }

    get offsetX(): number {
        return this.panelSource.offsetX;
    }

    get offsetY(): number {
        return this.panelSource.offsetY;
    }

    get elementName(): string {
        return this.panelSource.elementName;
    }

    get userUniqueId(): number {
        return this.panelSource.userUniqueId;
    }

    get panelBoundaryLoop(): BoundaryLoop {
        return this._panelBoundaryLoop;
    }

    createGeometry(): ExtrusionGeometry {
        return this.claddingExtrusionFactory.createCladdingCellExtrusion(this.panelBoundaryLoop, {
            thickness: this.thickness,
            material: this.material,
        });
    }

    computePanelTypeTitleMatrix(titleSize: THREE.Vector3): THREE.Matrix4 {
        const panelTransform = this.panelBoundaryLoop.transform;

        const panelBox = this.panelBoundaryLoop.box;

        const position = new THREE.Vector3(
            0.5 * (panelBox.min.x + panelBox.max.x - titleSize.x),
            0.5 * (panelBox.min.y + panelBox.max.y - titleSize.y),
            editorCladdingGeometryThickness + zFightingFixDistance,
        ).applyMatrix4(panelTransform);

        return new THREE.Matrix4().copy(panelTransform).setPosition(position);
    }

    getPanelGeometryCoordinateSystem(): THREE.Matrix4 {
        const { transform, box } = this.panelBoundaryLoop;

        const position = box
            .getCenter()
            .add(new THREE.Vector3(0, 0, 0.5 * editorCladdingGeometryThickness))
            .applyMatrix4(transform);

        return new THREE.Matrix4().copy(transform).setPosition(position);
    }

    getOffsetGizmoCoordinateSystem(): THREE.Matrix4 {
        const transform = this.panelBoundaryLoop.transform;

        const localPosition = this.panelBoundaryLoop.getBottomLeftCorner();
        localPosition.z = 0.5 * editorCladdingGeometryThickness;

        const position = localPosition.applyMatrix4(transform);

        return new THREE.Matrix4()
            .multiplyMatrices(transform, new THREE.Matrix4().makeRotationZ(Math.PI))
            .setPosition(position);
    }

    setPanelType(targetPanelTypeId: string | { id: string }) {
        this.panelSource.customPanelTypeId = typeof targetPanelTypeId === "string"
            ? targetPanelTypeId
            : targetPanelTypeId.id;
    }

    setPanelWindowDesignParameters() {
        // No windows in custom panels
        console.error("Custom panels do not have windows!");
    }

    setLouvreCutouts() {
        // No louvres in custom panels
        console.error("Custom panels do not have louvres!");
    }

    update({ elementName, installationSequence }: PanelParameters) {
        this.panelSource.elementName = elementName;
        this._installationSequence = installationSequence;
    }

    setOffset(offset: UpdateCustomPanelOffsetCommand) {
        const { offsetX, offsetY } = offset;
        this.panelSource.offsetX = offsetX;
        this.panelSource.offsetY = offsetY;

        const box = new THREE.Box3(
            new THREE.Vector3(offsetX, offsetY, 0),
            new THREE.Vector3(offsetX + this.length, offsetY + this.height, 0),
        );

        this._panelBoundaryLoop = boundaryLoopsFactory.createLoopFromBox(box, this.panelBoundaryLoop.transform);
    }

    getWorldOrigin(targetVector?: THREE.Vector3): THREE.Vector3 {
        return this.panelBoundaryLoop.toWorldCoordinates(this.panelBoundaryLoop.box.min, targetVector);
    }

    getWorldNormal(target?: THREE.Vector3): THREE.Vector3 {
        target = target || new THREE.Vector3();

        return target.setFromMatrixColumn(2, this.panelBoundaryLoop.transform);
    }
}

const boundaryLoopsFactory = new BoundaryLoopsFactory();
